import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Shield, Search, GraduationCap } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useSearch } from "../contexts/SearchContext";
import Brand from "./Brand";

export default function Header() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const { openSearch } = useSearch();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-bone/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Brand size="sm" linkTo="/dashboard" />

        {user && (
          <div className="flex items-center gap-3">
            <button
              onClick={openSearch}
              data-testid="open-search"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground bg-white border border-border rounded-full ps-3 pe-2 py-1.5 hover:bg-bone hover:border-sage/40 transition-colors"
              title="Search the course"
            >
              <Search className="w-4 h-4" strokeWidth={1.8} />
              <span className="hidden sm:inline">Search</span>
              <span className="hidden md:inline text-[10px] uppercase tracking-wider bg-secondary px-1.5 py-0.5 rounded font-mono">⌘K</span>
            </button>
            {user.role === "admin" && (
              <Link
                to="/admin"
                data-testid="admin-console-link"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-sage text-white hover:bg-sage-dark transition-colors uppercase tracking-[0.15em]"
                title="Admin Console"
              >
                <Shield className="w-3.5 h-3.5" strokeWidth={2} />
                Admin
              </Link>
            )}
            {user.role === "teacher" && (
              <Link
                to="/teacher"
                data-testid="teacher-console-link"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-terracotta text-white hover:bg-terracotta/90 transition-colors uppercase tracking-[0.15em]"
                title="Teacher Console"
              >
                <GraduationCap className="w-3.5 h-3.5" strokeWidth={2} />
                Teacher
              </Link>
            )}
            <div className="hidden sm:flex items-center gap-2.5 ps-3 pe-4 py-1.5 rounded-full bg-white border border-border">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-sage text-white text-xs flex items-center justify-center">
                  {user.name?.[0] || "U"}
                </div>
              )}
              <span className="text-sm font-medium text-foreground" data-testid="user-name">
                {user.name?.split(" ")[0]}
              </span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-sage transition-colors px-3 py-1.5 rounded-lg hover:bg-white"
              aria-label={t("logout")}
            >
              <LogOut className="w-4 h-4" strokeWidth={1.8} />
              <span className="hidden sm:inline">{t("logout")}</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
